// Helper de exportação Excel estilizado (paleta VigorHub olive/amber).
// Reusa o padrão que existia no antigo KPIDashboard, agora centralizado.
import XLSX from 'xlsx-js-style';

// 'brl' = moeda com centavos; 'data' = serial de data do Excel (ver `dataParaSerialExcel`),
// para a coluna ordenar e filtrar como data e não como texto.
export type ColType = 'txt' | 'num' | 'pct' | 'kwh' | 'brl' | 'data';

const HDR_STYLE = { font: { bold: true, color: { rgb: 'FFFFFF' }, name: 'Calibri', sz: 11 }, fill: { fgColor: { rgb: '4A4E3F' } }, alignment: { horizontal: 'center' as const, vertical: 'center' as const }, border: { bottom: { style: 'medium' as const, color: { rgb: '6B705C' } } } };
const TOTAL_STYLE = { font: { bold: true, name: 'Calibri', sz: 10, color: { rgb: '4A4E3F' } }, fill: { fgColor: { rgb: 'E8EAE3' } }, border: { top: { style: 'medium' as const, color: { rgb: '6B705C' } }, bottom: { style: 'medium' as const, color: { rgb: '6B705C' } } } };
const ALT_FILL = { fgColor: { rgb: 'F5F5F0' } };

const numFmtFor = (t: ColType) =>
    t === 'pct' ? '0.0"%"'
        : t === 'kwh' ? '#,##0.00'
            : t === 'num' ? '#,##0'
                : t === 'brl' ? '"R$" #,##0.00'
                    : t === 'data' ? 'dd/mm/yyyy'
                        : undefined;

// São 8 combinações possíveis (4 tipos × zebrado), mas um objeto novo por célula
// custa caro: o export do CRM chega a 500 mil células. O writer só LÊ `cell.s`,
// então compartilhar a mesma instância entre células é seguro.
const CACHE_ESTILO = new Map<string, any>();

const cellStyle = (t: ColType, alt: boolean) => {
    const chave = `${t}|${alt}`;
    const cacheado = CACHE_ESTILO.get(chave);
    if (cacheado) return cacheado;

    const base: any = { font: { name: 'Calibri', sz: 10 }, alignment: { horizontal: t === 'txt' ? 'left' : t === 'data' ? 'center' : 'right' } };
    const fmt = numFmtFor(t);
    if (fmt) base.numFmt = fmt;
    if (alt) base.fill = { ...ALT_FILL };
    CACHE_ESTILO.set(chave, base);
    return base;
};

const enc = (r: number, c: number) => XLSX.utils.encode_cell({ r, c });

/** Constrói uma worksheet estilizada a partir de cabeçalhos + linhas (matriz) + tipos por coluna. */
export const buildSheet = (
    headers: string[],
    rows: (string | number)[][],
    colTypes: readonly ColType[],
    opts?: { totals?: (string | number)[]; widths?: number[]; autofilter?: boolean },
): XLSX.WorkSheet => {
    const aoa: (string | number)[][] = [headers, ...rows];
    if (opts?.totals) aoa.push(opts.totals);
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    headers.forEach((_, c) => { const ref = enc(0, c); if (ws[ref]) ws[ref].s = HDR_STYLE; });
    rows.forEach((_, r) => {
        const rr = r + 1;
        colTypes.forEach((t, c) => { const ref = enc(rr, c); if (ws[ref]) ws[ref].s = cellStyle(t, rr % 2 === 0); });
    });
    if (opts?.totals) {
        const rr = rows.length + 1;
        colTypes.forEach((t, c) => {
            const ref = enc(rr, c);
            if (!ws[ref]) return;
            const fmt = numFmtFor(t);
            ws[ref].s = { ...TOTAL_STYLE, ...(fmt ? { numFmt: fmt } : {}), alignment: { horizontal: t === 'txt' ? 'left' as const : 'right' as const } };
        });
    }
    ws['!cols'] = (opts?.widths ?? headers.map(h => Math.max(12, h.length + 2))).map(wch => ({ wch }));
    // Filtro no cabeçalho — indispensável quando a aba passa de algumas dezenas de
    // colunas. A linha de totais fica de fora do intervalo para não ser filtrada junto.
    if (opts?.autofilter && headers.length > 0) {
        ws['!autofilter'] = {
            ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: headers.length - 1 } }),
        };
    }
    return ws;
};

/** Cria o workbook com as abas informadas e dispara o download. */
export const downloadWorkbook = (sheets: { name: string; ws: XLSX.WorkSheet }[], filename: string) => {
    const wb = XLSX.utils.book_new();
    sheets.forEach(s => XLSX.utils.book_append_sheet(wb, s.ws, s.name.slice(0, 31)));
    // `compression` é FALSE por padrão no SheetJS: o .xlsx sai como um zip que só
    // empacota, sem deflate. Em export grande isso é a diferença entre 224 MB e
    // 20 MB — o XML de planilha é repetitivo e comprime perto de 10:1.
    XLSX.writeFile(wb, filename, { compression: true });
};

/** Timestamp pt-BR para nome de arquivo: dd-mm-aaaa_hhmmss */
export const fileStamp = () => {
    const now = new Date();
    return `${now.toLocaleDateString('pt-BR').replace(/\//g, '-')}_${now.toLocaleTimeString('pt-BR').replace(/:/g, '')}`;
};
